import { PublicationItem } from 'components/discover/publications/model';
import { FC, useCallback } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';

import { VideoItem } from '../model';
import { PlayList, PlayListButton } from './play-list';
import { VideoThumbnail } from './video-thumbnail';

type VideoListProps = Readonly<{
  onSelectVideo: (videoId: string, autoPlay: boolean) => void;
  activeItems: ReadonlyArray<PublicationItem>;
  activeVideo: VideoItem;
  isPlaying: boolean;
}>;

export const VideoList: FC<VideoListProps> = ({ activeItems, activeVideo, onSelectVideo, isPlaying }) => {
  const activeVideoIndex = Math.max(
    activeItems.findIndex(i => i.videoId === activeVideo.videoId),
    0,
  );
  const hasPrevious = activeVideoIndex > 0;
  const hasNext = activeVideoIndex < activeItems.length - 1;

  const onPrevious = useCallback(() => {
    if (hasPrevious) {
      const previousVideoId = activeItems[activeVideoIndex - 1].videoId!;
      onSelectVideo(previousVideoId, false);
      logAnalytics.trackEvent('Discover', 'See more videos - Slide video list left');
    }
  }, [activeVideoIndex, activeItems, onSelectVideo, hasPrevious]);
  const onNext = useCallback(() => {
    if (hasNext) {
      const nextVideoId = activeItems[activeVideoIndex + 1].videoId!;
      onSelectVideo(nextVideoId, false);
      logAnalytics.trackEvent('Discover', 'See more videos - Slide video list right');
    }
  }, [activeVideoIndex, activeItems, onSelectVideo, hasNext]);

  return (
    <div
      className="track align-items-stretch overflow-hidden border-secondary border-top border-opacity-70"
      role="list"
    >
      <PlayListButton type="prev" onClick={onPrevious} disabled={!hasPrevious} />
      <PlayListButton type="next" onClick={onNext} disabled={!hasNext} />
      <PlayList $activeIndex={activeVideoIndex}>
        <div className="slider-nav d-flex overflow-hidden p-3">
          {activeItems.map(({ title, text, imageUrl, videoId }) => (
            <VideoThumbnail
              active={videoId === activeVideo.videoId}
              isPlaying={isPlaying}
              key={title}
              title={title}
              subTitle={text}
              thumbnailSrc={imageUrl ?? `https://socgen.qumucloud.com/kulu/${videoId}/thumbnail`}
              onClick={() => onSelectVideo(videoId!, true)}
            />
          ))}
        </div>
      </PlayList>
    </div>
  );
};
